import React, { useState, useEffect, useCallback } from 'react';
import { MessageSquare, Plus, Eye, Edit2, Trash2, Send, Calendar, Search, Users, BarChart3, ArrowLeft, Save, Clock } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from 'axios';

const SmsCampaigns = ({ contacts, settings }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [campaignStats, setCampaignStats] = useState({
    totalCampaigns: 0,
    sentCampaigns: 0,
    totalSent: 0
  });
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [campaignFilters, setCampaignFilters] = useState({
    search: '',
    status: '',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // SMS Builder States
  const [smsForm, setSmsForm] = useState({
    name: '',
    message: '',
    contactIds: [],
    scheduleType: 'now',
    scheduledDate: '',
    scheduledTime: '',
    template: 'custom'
  });

  // SMS Templates
  const smsTemplates = {
    welcome: {
      name: 'Welcome Message',
      message: "Welcome to Ella's Place! We're excited to help you look & feel amazing. Book your appointment: [booking_link] Reply STOP to opt out."
    },
    appointment_reminder: {
      name: 'Appointment Reminder',
      message: "Hi [Name], reminder: Your appointment at Ella's Place is tomorrow at [time]. See you soon! Reply STOP to opt out."
    },
    promotion: {
      name: 'Promotional Offer',
      message: "🌟 Special offer! Get [discount]% off all services this week at Ella's Place. Book now: [booking_link] Reply STOP to opt out."
    },
    birthday: {
      name: 'Birthday Wish',
      message: "Happy Birthday [Name]! 🎉 Celebrate with us - enjoy 20% off any service this month. Book: [booking_link] Reply STOP to opt out."
    },
    follow_up: {
      name: 'Post-Service Follow-up',
      message: "Hi [Name], thanks for visiting Ella's Place! How was your experience? We'd love your feedback. Reply STOP to opt out."
    },
    reactivation: {
      name: 'Customer Reactivation',
      message: "We miss you at Ella's Place! Come back & enjoy 15% off your next visit. Book now: [booking_link] Reply STOP to opt out."
    }
  };

  // Fetch campaigns
  const fetchCampaigns = useCallback(async () => {
    try {
      const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/comms-marketing/sms-campaigns`, {
        params: campaignFilters
      });
      setCampaigns(response.data.campaigns || []);
      setCampaignStats(response.data.stats || {});
    } catch (err) {
      console.error('Failed to load SMS campaigns:', err);
      toast.error('Failed to load SMS campaigns');
    }
  }, [campaignFilters]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  // Form handlers
  const handleFormChange = useCallback((field, value) => {
    setSmsForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleTemplateSelect = (templateKey) => {
    const template = smsTemplates[templateKey];
    setSmsForm(prev => ({
      ...prev,
      template: templateKey,
      message: template.message
    }));
  };

  const handleSaveCampaign = async () => {
    if (!smsForm.name || !smsForm.message || !smsForm.contactIds.length) {
      toast.error('Name, message, and contacts are required');
      return;
    }

    if (smsForm.message.length > 160) {
      toast.error('SMS message must be 160 characters or less');
      return;
    }

    if (smsForm.scheduleType === 'later' && (!smsForm.scheduledDate || !smsForm.scheduledTime)) {
      toast.error('Please set date and time for scheduled messages');
      return;
    }

    try {
      const campaignData = {
        name: smsForm.name,
        message: smsForm.message,
        contactIds: smsForm.contactIds,
        status: smsForm.scheduleType === 'now' ? 'sent' : smsForm.scheduleType === 'later' ? 'scheduled' : 'draft',
        recipients: smsForm.scheduleType === 'now' ? smsForm.contactIds.length : 0,
        sentAt: smsForm.scheduleType === 'now' ? new Date() : null,
        scheduledAt: smsForm.scheduleType === 'later' ? new Date(`${smsForm.scheduledDate}T${smsForm.scheduledTime}`) : null,
      };

      let res;
      if (editingCampaign) {
        res = await axios.put(`${import.meta.env.VITE_API_URL}/api/comms-marketing/sms-campaigns/${editingCampaign._id}`, campaignData);
      } else {
        res = await axios.post(`${import.meta.env.VITE_API_URL}/api/comms-marketing/sms-campaigns`, campaignData);
      }

      resetBuilder();
      await fetchCampaigns();
      toast.success(editingCampaign ? 'SMS campaign updated' : 'SMS campaign created');
    } catch (err) {
      console.error('SMS save error:', err);
      toast.error('Failed to save SMS campaign: ' + (err.response?.data?.message || err.message));
    }
  };

  const resetBuilder = () => {
    setShowBuilder(false);
    setEditingCampaign(null);
    setSmsForm({
      name: '',
      message: '',
      contactIds: [],
      scheduleType: 'now',
      scheduledDate: '',
      scheduledTime: '',
      template: 'custom'
    });
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this SMS campaign?')) return;

    try {
      await axios.delete(`${import.meta.env.VITE_API_URL}/api/comms-marketing/sms-campaigns/${campaignId}`);
      await fetchCampaigns();
      toast.success('SMS campaign deleted successfully');
    } catch (err) {
      toast.error('Failed to delete SMS campaign: ' + (err.response?.data?.message || err.message));
    }
  };

  // Utility functions
  const formatDate = (date) => date ? new Date(date).toLocaleDateString() : '-';
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'sent': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      case 'sending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Filter and pagination
  const phoneContacts = contacts.filter(c => c.phone);
  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.name.toLowerCase().includes(campaignFilters.search.toLowerCase());
    const matchesStatus = campaignFilters.status ? campaign.status === campaignFilters.status : true;
    return matchesSearch && matchesStatus;
  });

  const paginatedCampaigns = filteredCampaigns.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const totalPages = Math.ceil(filteredCampaigns.length / itemsPerPage);

  // Character count for message
  const remainingChars = 160 - smsForm.message.length;

  // SMS Builder Component
  const renderSmsBuilder = () => (
    <div className="space-y-6">
      {/* Builder Header */}
      <div className="flex items-center justify-between bg-white rounded-lg shadow-md p-4">
        <div className="flex items-center gap-4">
          <button
            onClick={resetBuilder}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
          >
            <ArrowLeft size={20} />
            Back to SMS Campaigns
          </button>
          <h2 className="text-xl font-semibold text-gray-900">
            {editingCampaign ? 'Edit SMS Campaign' : 'Create SMS Campaign'}
          </h2>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => handleFormChange('scheduleType', 'draft')}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 flex items-center gap-2"
          >
            <Save size={16} />
            Save Draft
          </button>
          <button
            onClick={handleSaveCampaign}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center gap-2"
          >
            <Send size={16} />
            {smsForm.scheduleType === 'now' ? 'Send Now' : 'Save Campaign'}
          </button>
        </div>
      </div>

      {/* Builder Content */}
      <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
        {/* Template Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Choose SMS Template</label>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.entries(smsTemplates).map(([key, template]) => (
              <div
                key={key}
                onClick={() => handleTemplateSelect(key)}
                className={`border-2 rounded-lg p-3 cursor-pointer transition-colors ${
                  smsForm.template === key
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <h4 className="font-medium text-gray-900 text-sm">{template.name}</h4>
                <p className="text-xs text-gray-600 mt-1 truncate">{template.message.substring(0, 50)}...</p>
              </div>
            ))}
            <div
              onClick={() => handleTemplateSelect('custom')}
              className={`border-2 rounded-lg p-3 cursor-pointer transition-colors ${
                smsForm.template === 'custom'
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <h4 className="font-medium text-gray-900 text-sm">Custom Message</h4>
              <p className="text-xs text-gray-600 mt-1">Write your own SMS</p>
            </div>
          </div>
        </div>

        {/* Campaign Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Campaign Name *</label>
            <input
              type="text"
              value={smsForm.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="e.g., Valentine's Day Promo"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SMS Message * 
              <span className={`ml-2 text-sm ${remainingChars < 0 ? 'text-red-600' : remainingChars < 20 ? 'text-yellow-600' : 'text-gray-500'}`}>
                ({remainingChars} characters remaining)
              </span>
            </label>
            <textarea
              value={smsForm.message}
              onChange={(e) => handleFormChange('message', e.target.value)}
              maxLength="160"
              rows="4"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                remainingChars < 0 ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Hi [Name], special offer just for you! Book now: [booking_link] Reply STOP to opt out."
            />
            <div className="mt-2 text-xs text-gray-500">
              <p>Available variables: [Name], [booking_link], [time], [discount]</p>
              <p>Always include "Reply STOP to opt out" for compliance</p>
            </div>
          </div>
        </div>

        {/* Recipients Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Select Recipients *</label>
          <div className="border border-gray-300 rounded-md p-4 max-h-64 overflow-y-auto">
            <div className="mb-3 flex items-center justify-between">
              <button
                type="button"
                onClick={() => {
                  const allSelected = phoneContacts.every(c => smsForm.contactIds.includes(c._id));
                  handleFormChange('contactIds', 
                    allSelected ? [] : phoneContacts.map(c => c._id)
                  );
                }}
                className="text-sm text-purple-600 hover:text-purple-800"
              >
                {phoneContacts.filter(c => smsForm.contactIds.includes(c._id)).length === phoneContacts.length 
                  ? 'Deselect All' : 'Select All with Phone'}
              </button>
              <span className="text-sm text-gray-600">
                {phoneContacts.length} contacts with phone numbers
              </span>
            </div>
            
            {phoneContacts.length > 0 ? (
              phoneContacts.map((contact) => (
                <div key={contact._id} className="flex items-center mb-2">
                  <input
                    id={`sms-contact-${contact._id}`}
                    type="checkbox"
                    checked={smsForm.contactIds.includes(contact._id)}
                    onChange={(e) => {
                      const contactIds = e.target.checked
                        ? [...smsForm.contactIds, contact._id]
                        : smsForm.contactIds.filter((id) => id !== contact._id);
                      handleFormChange('contactIds', contactIds);
                    }}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  />
                  <label htmlFor={`sms-contact-${contact._id}`} className="ml-3 block text-sm text-gray-900">
                    <div className="font-medium">{contact.name}</div>
                    <div className="text-gray-500">{contact.phone}</div>
                  </label>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <MessageSquare size={32} className="mx-auto text-gray-400 mb-2" />
                <p className="text-gray-600">No contacts with phone numbers found</p>
                <p className="text-sm text-gray-500 mt-1">Add contacts with phone numbers to send SMS campaigns</p>
              </div>
            )}
          </div>
          
          {smsForm.contactIds.length > 0 && (
            <div className="mt-3 bg-purple-50 border border-purple-200 rounded-md p-3">
              <p className="text-sm text-purple-800">
                <strong>{smsForm.contactIds.length}</strong> recipients selected
              </p>
            </div>
          )}
        </div>

        {/* Schedule Options */}
        <div className="border border-gray-200 rounded-md p-4">
          <h4 className="text-base font-medium text-gray-900 mb-4">Schedule Options</h4>
          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <input
                  id="sms-send-now"
                  type="radio"
                  value="now"
                  checked={smsForm.scheduleType === 'now'}
                  onChange={(e) => handleFormChange('scheduleType', e.target.value)}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                />
                <label htmlFor="sms-send-now" className="ml-2 block text-sm text-gray-900">
                  Send immediately
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="sms-save-draft"
                  type="radio"
                  value="draft"
                  checked={smsForm.scheduleType === 'draft'}
                  onChange={(e) => handleFormChange('scheduleType', e.target.value)}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                />
                <label htmlFor="sms-save-draft" className="ml-2 block text-sm text-gray-900">
                  Save as draft
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="sms-schedule-later"
                  type="radio"
                  value="later"
                  checked={smsForm.scheduleType === 'later'}
                  onChange={(e) => handleFormChange('scheduleType', e.target.value)}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                />
                <label htmlFor="sms-schedule-later" className="ml-2 block text-sm text-gray-900">
                  Schedule for later
                </label>
              </div>
            </div>

            {smsForm.scheduleType === 'later' && (
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                  <input
                    type="date"
                    value={smsForm.scheduledDate}
                    onChange={(e) => handleFormChange('scheduledDate', e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Time</label>
                  <input
                    type="time"
                    value={smsForm.scheduledTime}
                    onChange={(e) => handleFormChange('scheduledTime', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* SMS Preview */}
        <div className="border border-gray-200 rounded-md p-4">
          <h4 className="text-base font-medium text-gray-900 mb-3">SMS Preview</h4>
          <div className="bg-gray-100 rounded-lg p-4 max-w-sm">
            <div className="bg-white rounded-lg p-3 shadow-sm">
              <div className="text-xs text-gray-500 mb-2">SMS from Ella's Place</div>
              <div className="text-sm text-gray-900">
                {smsForm.message.replace(/\[Name\]/g, 'John Doe')
                  .replace(/\[booking_link\]/g, 'ellasmgt.com/book')
                  .replace(/\[time\]/g, '2:00 PM')
                  .replace(/\[discount\]/g, '20') || 'Your message will appear here...'}
              </div>
            </div>
          </div>
        </div>

        {/* SMS Settings Warning */}
        {!settings?.sms?.enabled && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
            <div className="flex items-start">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-amber-800">SMS Not Configured</h3>
                <div className="mt-2 text-sm text-amber-700">
                  <p>SMS functionality is not enabled. Go to Settings to configure your SMS provider before sending campaigns.</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Main SMS Campaign List View
  if (showBuilder) {
    return renderSmsBuilder();
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-md p-4">
          <h3 className="text-base font-semibold text-gray-900 mb-2">Total SMS Campaigns</h3>
          <p className="text-2xl font-bold text-purple-600">{campaignStats.totalCampaigns || 0}</p>
          <p className="text-xs text-gray-600 mt-2">All time</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <h3 className="text-base font-semibold text-gray-900 mb-2">Sent Campaigns</h3>
          <p className="text-2xl font-bold text-green-600">{campaignStats.sentCampaigns || 0}</p>
          <p className="text-xs text-gray-600 mt-2">Successfully delivered</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <h3 className="text-base font-semibold text-gray-900 mb-2">Total Messages Sent</h3>
          <p className="text-2xl font-bold text-blue-600">{campaignStats.totalSent || 0}</p>
          <p className="text-xs text-gray-600 mt-2">SMS messages</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <h3 className="text-base font-semibold text-gray-900 mb-2">Phone Contacts</h3>
          <p className="text-2xl font-bold text-indigo-600">{phoneContacts.length || 0}</p>
          <p className="text-xs text-gray-600 mt-2">Available for SMS</p>
        </div>
      </div>

      {/* Action Bar */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex flex-wrap justify-between items-center gap-4">
          <button
            onClick={() => setShowBuilder(true)}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm flex items-center gap-2"
            disabled={!settings?.sms?.enabled}
          >
            <Plus size={16} />
            Create SMS Campaign
          </button>
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search campaigns..."
                value={campaignFilters.search}
                onChange={(e) => setCampaignFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
              />
            </div>
            <select
              value={campaignFilters.status}
              onChange={(e) => setCampaignFilters(prev => ({ ...prev, status: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
            >
              <option value="">All Status</option>
              <option value="draft">Draft</option>
              <option value="scheduled">Scheduled</option>
              <option value="sent">Sent</option>
            </select>
          </div>
          {!settings?.sms?.enabled && (
            <p className="text-sm text-amber-600">
              SMS is not configured. Go to Settings to set up SMS provider.
            </p>
          )}
        </div>
      </div>

      {/* Campaigns List */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">SMS Campaigns</h3>
          <p className="text-sm text-gray-600 mt-1">
            Showing {paginatedCampaigns.length} of {filteredCampaigns.length} campaigns
          </p>
        </div>
        
        {paginatedCampaigns.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Campaign
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recipients
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Delivery
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedCampaigns.map((campaign) => (
                  <tr key={campaign._id} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <div className="text-sm font-medium text-gray-900">{campaign.name}</div>
                      <div className="text-xs text-gray-500 truncate max-w-xs">{campaign.message}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(campaign.status)}`}>
                        {campaign.status}
                      </span>
                      {campaign.status === 'scheduled' && campaign.scheduledAt && (
                        <div className="text-xs text-gray-500 mt-1 flex items-center">
                          <Clock size={12} className="mr-1" />
                          {formatDate(campaign.scheduledAt)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {campaign.recipients || 0}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {campaign.status === 'sent' ? (
                        <div className="text-xs">
                          <div className="text-green-600">{campaign.delivered || 0} delivered</div>
                          {campaign.failed > 0 && (
                            <div className="text-red-600">{campaign.failed} failed</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(campaign.createdAt)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            alert(`SMS Campaign: ${campaign.name}\n\nMessage: ${campaign.message}\n\nRecipients: ${campaign.recipients || 0}\nStatus: ${campaign.status}`);
                          }}
                          className="text-blue-600 hover:text-blue-900"
                          title="Preview"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => {
                            setEditingCampaign(campaign);
                            setSmsForm({
                              name: campaign.name,
                              message: campaign.message,
                              contactIds: campaign.contactIds || [],
                              scheduleType: campaign.status === 'scheduled' ? 'later' : 'draft',
                              scheduledDate: campaign.scheduledAt ? new Date(campaign.scheduledAt).toISOString().split('T')[0] : '',
                              scheduledTime: campaign.scheduledAt ? new Date(campaign.scheduledAt).toTimeString().slice(0, 5) : '',
                              template: 'custom'
                            });
                            setShowBuilder(true);
                          }}
                          className="text-yellow-600 hover:text-yellow-900"
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteCampaign(campaign._id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-6 text-center">
            <MessageSquare size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No SMS Campaigns Yet</h3>
            <p className="text-gray-600 mb-4">
              Create your first SMS campaign to start engaging with customers via text messages.
            </p>
            <button
              onClick={() => setShowBuilder(true)}
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
              disabled={!settings?.sms?.enabled}
            >
              Create Your First SMS Campaign
            </button>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 flex justify-between items-center border-t border-gray-200">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => prev - 1)}
              className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">Page {currentPage} of {totalPages}</span>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => prev + 1)}
              className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SmsCampaigns;